import React, { useContext, useEffect, useState } from 'react';
import '../styles/ReviewModal.css';
import Review from '../interfaces/Review';
import Place from '../interfaces/Place';
import ReviewModalProps from '../interfaces/ReviewModalProps';
import GeoSearch from './GeoSearch';
import { Interface } from '../interfaces/Interface';
import LandingViewContext from '../context/LandingViewContext';
import AppContext from '../context/AppContext';
import dbActions from '../utils/db';
import { DocumentReference } from 'firebase/firestore';
import Rating from '@mui/material/Rating';


const AddReviewModal: React.FC<ReviewModalProps> = ({ showModal, onCloseModal, setShowToast, initialPlace }) => {
  const [place, setPlace] = useState<Place>(Interface.emptyInterface<Place>());
  const [hasError, setHasError] = useState<boolean>(false);
  const [hasErrorAdding, setHasErrorAdding] = useState<boolean>(false);
  const [seeMore, setSeeMore] = useState<boolean>(false);
  const [newReview, setReview] = useState<Review>(Interface.emptyInterface<Review>());
  const { setRefreshLocations } = useContext(LandingViewContext);
  const { user } = useContext(AppContext);

  const addPlaceEntry = async(place: Place): Promise<DocumentReference | undefined> => {
    try {
      const locationDocRef = await dbActions.addLocationIfNotExists(place)
      return locationDocRef;
    } catch (error) {
      console.error("Error adding place entry: ", error);
      return; // Return empty string in case of error
    }
  }

  const addReviewEntry = async(review: Review, locationDocRef: DocumentReference): Promise<DocumentReference> => { 
    return await dbActions.addReview(locationDocRef, review);
  }

  const addUserLocationEntry = async (locationId: string, reviewId: string) => {
    const userData = await dbActions.getUserFromEmail(user.email!);
    if (userData.user) {
      // Check if the user has already reviewed the location
      const existingLocation = userData.user.locationIds.find(loc => loc.locationId === locationId);

      if (existingLocation) {
          // If the location already exists, add the reviewId to its reviewIds array
          existingLocation.reviewIds.push(reviewId);
      } else {
          // If the location doesn't exist, add a new entry for it
          userData.user.locationIds.push({ locationId: locationId, reviewIds: [reviewId] });
      }
      // Update the user document with the modified userData
      dbActions.updateDocument(userData.docRef, userData.user)
    } else {
        // If the user document doesn't exist, create a new one with the location and review
        dbActions.addUser(user.email!, locationId, reviewId);
    }
  }
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newReview.review || !newReview.score || Interface.isEmpty(place)) {
      setHasError(true);
      return;
    }
    try {
      const locationId = await addPlaceEntry(place);
      const reviewId = await addReviewEntry(newReview, locationId!);
      addUserLocationEntry(locationId!.id, reviewId.id);
      setRefreshLocations(true);
      setShowToast(true);
      handleCloseModal();
    } catch (error) {
      setHasErrorAdding(true);
    }
  };

  const handleCloseModal = () => {
    setReview(Interface.emptyInterface<Review>());
    setPlace(Interface.emptyInterface<Place>());
    setHasError(false);
    setSeeMore(false);
    setHasErrorAdding(false);
    onCloseModal();
  }

  useEffect(() => {
    if (initialPlace !== null || !Interface.isEmpty(initialPlace))
      setPlace(initialPlace as Place);
  }, [])

  return showModal ? (
    <div className="modal">
      <div className="modal-content">
        <span className="close" onClick={handleCloseModal}>&times;</span>
        <h2>Agregar Reseña</h2>
        <form onSubmit={handleSubmit}>
          { seeMore ||
            (<>
              <label>
                Dirección:
              </label>
              <GeoSearch setPlace={setPlace} initialPlace={place}/>
              <label>
                Dirección adicional (numero de departamento/unidad funcional, si corresponde):
              </label>
              <input
                type='text'
                value={newReview.additional_details}
                onChange={(e) => setReview({ ...newReview, additional_details: e.target.value })}
              />
              <label>
                Reseña:
              </label>
              <textarea
                value={newReview.review}
                required={true}
                onChange={(e) => setReview({ ...newReview, review: e.target.value })}
                placeholder='Conta tu experiencia viviendo aqui: cuanto tiempo estuviste, relación con el propietario, inmobiliaria, problemas o cosas a favor de la vivienda.'
              />
              <div className="score-input" style={{marginBottom: "0.1rem"}}>
                <label id='scoreLabel'>Calificación</label>
                <Rating
                  name="simple-controlled"
                  value={newReview.score}
                  onChange={(event, newValue) => {
                    setReview({ ...newReview, score: newValue!})
                  }}
                />
                {hasError && <p>Los campos dirección, reseña y calificación son requeridos.</p>}
                {hasErrorAdding && <p>Hubo un error agregando tu reseña, inténtalo de nuevo.</p>}
              </div>
              <button type="button" className='show-more-btn' onClick={() => setSeeMore(true)}>Dar más detalles</button>
            </>)
          }
          {
            seeMore && (
              <>
                <div className="directOwner">
                <label>
                  Contrato por Dueño directo:
                </label>
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={newReview.directOwner || false}
                  onChange={(e) => setReview({ ...newReview, directOwner: e.target.checked })}
                />
              </div>
              <div className="relationshipWith">
              {newReview.directOwner && (
                <div className="relation-input">
                  <label>
                    Relación con Propietario:
                  </label>
                  <input
                    type="text"
                    value={newReview.relationshipWithOwner || ''}
                    onChange={(e) => setReview({ ...newReview, relationshipWithOwner: e.target.value })}
                  />
                </div>
              )}
              {!newReview.directOwner && (
                <div className="relation-input">
                  <label>
                    Relación con Inmobiliaria/Propietario:
                  </label>
                  <input
                    type="text"
                    value={newReview.relationshipWithAgent || ''}
                    onChange={(e) => setReview({ ...newReview, relationshipWithAgent: e.target.value })}
                  />
                </div>
              )}
              </div>
              <label>
                Comentarios Positivos:
              </label>
              <textarea
                value={newReview.positiveComments || ''}
                onChange={(e) => setReview({ ...newReview, positiveComments: e.target.value })}
                placeholder='Comparte tus comentarios positivos sobre la propiedad.'
              />
              <label>
                Comentarios Negativos:
              </label>
              <textarea
                value={newReview.negativeComments || ''}
                onChange={(e) => setReview({ ...newReview, negativeComments: e.target.value })}
                placeholder='Comparte tus comentarios negativos sobre la propiedad.'
              />
              <button type="button" className='show-more-btn' onClick={() => setSeeMore(false)}>Volver</button>
            </>
            )
          }
          <button type="submit" onClick={handleSubmit}>Enviar</button>
        </form>
      </div>
    </div>
  ) : null;
};

export default AddReviewModal;

